import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetRecordTypeQuery, useListRecordsQuery } from '@platform/state/api';
import { statusColor } from '@platform/core/lib/statusColors';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { codeQuery } from '../options/codeQuery';
import { recordNumber } from '../../views/recordNumber';
import { AdvancedSearchDialog } from './AdvancedSearchDialog';
import type { IRecordSearchDialogProps } from './IRecordSearchDialogProps';
import { useDebouncedValue } from './useDebouncedValue';

const LIMIT = 100;

/**
 * Розширений пошук запису для поля-посилання (прототип lkAdv kind record): пошук за кодом або назвою на сервері, колонки
 * ID · Назва · Статус · Виконавець (якщо тип має виконавця), перші 100 збігів; клік по рядку вибирає запис.
 */
export function RecordSearchDialog({ ws, target, title, exclude, onPick, onClose }: IRecordSearchDialogProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { data: meta } = useGetRecordTypeQuery(target, { skip: !target });
  const q = useDebouncedValue(codeQuery(query, meta?.recordType.prefix), 200);
  const { data, isFetching } = useListRecordsQuery({ ws, rt: target, params: { q: q || undefined, pageSize: LIMIT } }, { skip: !target });
  const strings = useModuleStrings(meta?.module.module.key);
  const rt = meta?.recordType;
  const withAssignee = Boolean(rt?.fields.some((f) => f.key === 'assignee'));
  const rows = useMemo(() => (data?.items ?? []).filter((r) => !exclude?.(r)).map((r) => {
    const assignee = r.values['assignee'];
    const who = assignee && typeof assignee === 'object' && 'name' in assignee ? assignee.name : t('fieldControls.unassigned');
    return {
      key: r.id,
      cells: [
        { text: recordNumber(r), mono: true, muted: true },
        { text: r.title || t('fieldControls.untitled'), strong: true },
        { text: rt ? strings.status(target, r.status, rt.statuses.list.find((s) => s.value === r.status)?.label) : r.status, dot: rt ? statusColor(rt.statuses, r.status, target) : undefined },
        ...(withAssignee ? [{ text: who, muted: true }] : []),
      ],
      onPick: () => onPick(r.id),
    };
  }), [data, exclude, rt, strings, target, t, onPick, withAssignee]);
  const total = data?.total ?? 0;
  const count = t('fieldControls.records', { count: total }) + (total > LIMIT ? ` · ${t('fieldControls.showingFirst', { count: LIMIT })}` : '');
  return (
    <AdvancedSearchDialog title={title} count={count} query={query} onQuery={setQuery} loading={isFetching}
      columns={['ID', t('fieldControls.colTitle'), t('fieldControls.colStatus'), ...(withAssignee ? [t('fieldControls.colAssignee')] : [])]}
      template={withAssignee ? '100px minmax(220px,2fr) 150px 160px' : '100px minmax(220px,2fr) 170px'} rows={rows} onClose={onClose} />
  );
}
